import { useState, useRef, useEffect } from "react";

const VISIBLE_COUNT = 12;
const ITEM_GAP = 44;
const CURVE_X = 28;

const RadialCategoryMenu = ({
  open,
  categories = [],
  selectedCatId,
  onSelect,
  onToggle,
}) => {
  /* -------------------- HOOKS -------------------- */

  const startY = useRef(0);

  const initialIndex = Math.max(
    categories.findIndex((c) => c.id === selectedCatId),
    0
  );

  const [centerIndex, setCenterIndex] = useState(initialIndex);

  /* 🔒 LOCK BACKGROUND */
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
    } else {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    };
  }, [open]);

  useEffect(() => {
    if (initialIndex !== centerIndex) {
      setCenterIndex(initialIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCatId]);

  if (!open || !categories.length) return null;

  const half = Math.floor(VISIBLE_COUNT / 2);
  const swipeThreshold = 25;

  /* -------------------- TOUCH (NO preventDefault) -------------------- */

  const handleTouchStart = (e) => {
    startY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    const deltaY = e.touches[0].clientY - startY.current;

    if (Math.abs(deltaY) > swipeThreshold) {
      setCenterIndex((prev) => {
        const next = deltaY > 0 ? prev - 1 : prev + 1;
        if (next < 0) return categories.length - 1;
        if (next >= categories.length) return 0;
        return next;
      });
      startY.current = e.touches[0].clientY;
    }
  };

  /* -------------------- BUILD ITEMS -------------------- */

  const visibleItems = [];
  for (let i = -half; i <= half; i++) {
    const index =
      (centerIndex + i + categories.length) % categories.length;

    visibleItems.push({
      ...categories[index],
      offset: i,
    });
  }

  /* -------------------- UI -------------------- */

  return (
    <>
      {/* OVERLAY */}
      <div
        className="fixed inset-0 bg-black/40 z-40 pointer-events-auto"
        onClick={onToggle}
      />

      {/* WHEEL */}
      <div
        className="fixed left-20 top-1/2 -translate-y-1/2 z-50
                   touch-none pointer-events-auto"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
      >
        <div className="relative">
          {visibleItems.map((cat) => {
            const y = cat.offset * ITEM_GAP;
            const x =
              cat.offset === 0
                ? 0
                : Math.abs(cat.offset) * CURVE_X;

            const isCenter = cat.offset === 0;

            return (
              <button
                key={cat.id}
                onClick={() => {
                  onSelect(cat.id);
                  setCenterIndex(
                    categories.findIndex((c) => c.id === cat.id)
                  );
                }}
                className={`absolute min-w-[88px] px-3 py-1.5 rounded-full
                  text-[11px] font-medium text-center whitespace-nowrap
                  transition-all duration-300 ease-out
                  ${
                    isCenter
                      ? "bg-orange-500 text-white scale-[1.05] shadow-md z-10"
                      : "bg-black text-white scale-[0.9] opacity-70"
                  }`}
                style={{
                  transform: `translate(${x}px, ${y}px)`,
                }}
              >
                {cat.name}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default RadialCategoryMenu;
